import React from "react";

// Who opens the conversation. Every value describes OUR test bot, never the
// agent under test — the vendor's own config uses confusingly similar wording
// for the opposite subject, so keep the "Our test bot" / "Your agent" phrasing
// in the labels.
export const OPENING_PROMPT_OPTIONS = [
  {
    value: "assistant-speaks-first",
    label: "Our test bot speaks first with a defined message",
  },
  {
    value: "assistant-speaks-first-with-model-generated-message",
    label: "Our test bot speaks first with a generated message",
  },
  {
    value: "assistant-waits-for-user",
    label: "Your agent speaks first — our test bot waits",
  },
];

const CONTROL_CLS =
  "w-full px-3 py-2 border border-input rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black";

/**
 * The single source of truth for the Opening Prompt control. Used by the
 * self-managed form, the pasted-instructions form and the synced-from-vendor
 * form so the three cannot drift apart.
 *
 * `bothSidesSilent` is a caller-supplied warning flag: the synced view knows
 * the vendor's agent stays quiet, so it can tell the user that choosing
 * "our test bot waits" leaves nobody to speak.
 */
const OpeningPromptField = ({
  id = "opening-prompt",
  type,
  message,
  onTypeChange,
  onMessageChange,
  hint = "How should our test agent start the conversation when calling your agent?",
  required = false,
  bothSidesSilent = false,
}) => (
  <div>
    <label
      htmlFor={id}
      className="block text-sm font-medium text-foreground mb-1"
    >
      Opening Prompt
      {required && <span className="text-destructive ml-1">*</span>}
    </label>
    {hint && <p className="text-sm text-muted-foreground mb-1.5">{hint}</p>}

    <select
      id={id}
      value={type}
      onChange={(e) => onTypeChange(e.target.value)}
      className={CONTROL_CLS}
    >
      {OPENING_PROMPT_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>

    {type === "assistant-speaks-first" && (
      <textarea
        id={`${id}-message`}
        aria-label="Opening message"
        value={message}
        onChange={(e) => onMessageChange(e.target.value)}
        className={`mt-3 ${CONTROL_CLS}`}
        rows={3}
        placeholder="Hi, I'm calling about..."
        required={required}
      />
    )}

    {bothSidesSilent && type === "assistant-waits-for-user" && (
      <p role="alert" className="mt-2 text-sm text-destructive">
        Your agent is configured to wait for the caller, so nobody would speak
        and the call would end in silence.
      </p>
    )}
  </div>
);

export default OpeningPromptField;
